import * as React from "react";

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface SelectBoxProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value"> {
  /** Current value. */
  value: string;
  /** Called with the selected value string. */
  onChange: (value: string) => void;
  /** Options to render. Omit and pass children for custom option markup. */
  options?: SelectOption[];
  /** Optional first option shown as a hint (empty value). */
  placeholder?: string;
  /** Disable the placeholder option to force a real choice. */
  placeholderDisabled?: boolean;
  /** Extra classes merged after "v3-sel". */
  className?: string;
  children?: React.ReactNode;
}

/**
 * Native select styled with the project's `.v3-sel` look.
 * Use `options` for the common case, or `children` when you need custom
 * ordering (e.g. options followed by a trailing "Self Created" entry).
 * Extra props (style, disabled, name, aria-*, etc.) pass through.
 */
export default function SelectBox({
  value,
  onChange,
  options,
  placeholder,
  placeholderDisabled = false,
  className = "",
  children,
  ...rest
}: SelectBoxProps) {
  return (
    <select
      {...rest}
      className={`v3-sel ${className}`.trim()}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {placeholder !== undefined && (
        <option value="" disabled={placeholderDisabled}>
          {placeholder}
        </option>
      )}
      {options
        ? options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))
        : children}
    </select>
  );
}